import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface ChangeStage {
  stage: string;
  title: string;
  description: string;
  icon: string;
  outcomes: string[];
}

interface TheoryOfChangeProps {
  stages: ChangeStage[];
}

const TheoryOfChange: React.FC<TheoryOfChangeProps> = ({ stages }) => {
  return (
    <section className="py-16 bg-card">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-4">
            Theory of Change
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            A systematic approach to transforming conflict from the individual to the global level
          </p>
        </div>

        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-primary via-secondary to-accent transform -translate-x-1/2" />

          <div className="space-y-12">
            {stages.map((stage, index) => (
              <div
                key={index}
                className={`relative grid lg:grid-cols-2 gap-8 items-center ${
                  index % 2 === 0 ? '' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Content */}
                <div className={`${index % 2 === 0 ? 'lg:text-right lg:pr-12' : 'lg:pl-12 lg:col-start-2'}`}>
                  <div className="bg-background p-8 rounded-xl shadow-md border border-border">
                    <div className="flex items-center space-x-3 mb-4">
                      <span className="text-sm font-cta font-bold text-accent uppercase tracking-wide">
                        {stage.stage}
                      </span>
                    </div>
                    <h3 className="text-2xl font-headline font-bold text-foreground mb-4">
                      {stage.title}
                    </h3>
                    <p className="text-muted-foreground font-body mb-6 leading-relaxed">
                      {stage.description}
                    </p>
                    <div className="space-y-3">
                      <p className="text-sm font-cta font-bold text-foreground uppercase">Key Outcomes:</p>
                      {stage.outcomes.map((outcome, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-foreground font-body">{outcome}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Icon Circle */}
                <div className={`${index % 2 === 0 ? 'lg:col-start-2' : 'lg:col-start-1 lg:row-start-1'} flex justify-center`}>
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg z-10 relative">
                      <Icon name={stage.icon as any} size={40} className="text-white" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TheoryOfChange;